import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { appService } from "../../App/app.service";

// Drilldown "locations" used by the existing RCC modals.
const LOCATIONS = {
  QUALIFIED_PIPELINE: "drilldownOnGraph",
  WON_OPPORTUNITIES: "drilldownOnGraphAttainment",
  OPP_DETAILS: "drilldownOppId",
};

// Action keys cards can call: onDrilldown(action, payload)
export const RCC_DRILLDOWN_ACTIONS = {
  DEFAULT: "default",
  WON_OPPORTUNITIES: "wonOpportunities",
  QUALIFIED_PIPELINE: "qualifiedPipeline",
};

/**
 * RCC drilldown controller.
 * Keeps drilldown modal state + async data fetching out of `Index.jsx`.
 *
 * Inputs: none (reads redux + uses appService)
 * Outputs: handlers + state needed to render drilldown modals
 */
export default function useRccDrilldowns() {
  const dispatch = useDispatch();

  // Drilldown state
  const [drilldownLocation, setDrilldownLocation] = useState("");
  const [secondLevelLocation, setSecondLevelLocation] = useState("");

  // Redux selectors for drilldown data
  const drilldownOnGraphData = useSelector(
    (state) => state.app.drilldownOnGraphData
  );
  const ExpectedAcvData = useSelector((state) => state.app.ExpectedAcvData);
  const EnsembleData = useSelector((state) => state.app.EnsembleData || "");
  const PipelineCoverageCFQ = useSelector(
    (state) => state.app.PipelineCoverageHomeCFQ || ""
  );
  const Value_Label = useSelector((state) => state.app.user?.Value_Label);
  const company = useSelector((state) => state.app.user?.company);
  const Win_Rate_Prefix = useSelector(
    (state) => state.app.user?.Win_Rate_Prefix
  );

  // Handler for QTD/YTD Bookings -> Won Opportunities drilldown
  const handleWonDrilldown = useCallback((timeFrame) => {
    setDrilldownLocation(LOCATIONS.WON_OPPORTUNITIES);
    dispatch({ type: "get_drilldown_on_graph_request" });

    appService.getAttainmentSectionDrilldown([], timeFrame).then(
      (json) => {
        let tempOpportunities = json.message.opportunities[1]
          .sort((a, b) => (a.ACV > b.ACV ? -1 : 1))
          .map((item, index) => ({ ...item, OppNo: index + 1 }));

        json.message = {
          ...json,
          columns: json.message.columns,
          count_acv: json.message.opportunities[0],
          opportunities: tempOpportunities,
        };
        dispatch({ type: "get_drilldown_on_graph_success", json });
      },
      (error) => {
        dispatch({
          type: "get_drilldown_on_graph_failure",
          error: typeof error === "object" ? "Something went wrong" : error,
        });
      }
    );
  }, [dispatch]);

  // Handler for Qtr Projection -> Qualified Pipeline drilldown
  const handleQualifiedPipelineDrilldown = useCallback(() => {
    setDrilldownLocation(LOCATIONS.QUALIFIED_PIPELINE);
    dispatch({ type: "get_drilldown_on_graph_request" });

    if (EnsembleData && EnsembleData.expectedHighMidLow) {
      // Use time series drilldown for ensemble model
      appService.getTimeSeriesHomeDrilldown([]).then(
        (json) => {
          let tempOpportunities = json.message.opportunities[1]
            .map((item, index) => ({ ...item, OppNo: index + 1 }))
            .sort((a, b) =>
              a.Acv_At_Time_T > b.Acv_At_Time_T ? -1 : 1
            )
            .map((item, index) => ({ ...item, OppNo: index + 1 }));

          json.message = {
            ...json.message,
            count_acv: json.message.opportunities[0],
            opportunities: tempOpportunities,
            columns: json.message.columns,
          };
          dispatch({ type: "get_drilldown_on_graph_success", json });
        },
        (error) => {
          dispatch({
            type: "get_drilldown_on_graph_failure",
            error: typeof error === "object" ? "Something went wrong" : error,
          });
        }
      );
    } else {
      // Fallback to pipeline coverage drilldown
      appService
        .getPipelineCoverageGenericDrilldown(
          [],
          "open",
          "CFQ",
          undefined,
          undefined
        )
        .then(
          (json) => {
            let tempOpportunities = json.message.opportunities[1]
              .sort((a, b) => (a.ACV > b.ACV ? -1 : 1))
              .map((item, index) => ({ ...item, OppNo: index + 1 }));

            json.message = {
              ...json,
              columns: json.message.columns,
              count_acv: json.message.opportunities[0],
              opportunities: tempOpportunities,
            };
            dispatch({ type: "get_drilldown_on_graph_success", json });
          },
          (error) => {
            dispatch({
              type: "get_drilldown_on_graph_failure",
              error: typeof error === "object" ? "Something went wrong" : error,
            });
          }
        );
    }
  }, [dispatch, EnsembleData]);

  // Back handler to close drilldowns
  const handleChangeLocationBack = useCallback(() => {
    setDrilldownLocation("");
    setSecondLevelLocation("");
    dispatch({ type: "clear_expected_acv_data" });
  }, [dispatch]);

  // Second level drilldown handler (for clicking on individual opportunity)
  const handleSecondLevelDrilldown = useCallback((location, oppId) => {
    setSecondLevelLocation(location);
    dispatch({ type: "get_expectedAcv_drilldown_data_request" });

    appService.getWaterfallOppData(oppId).then(
      (json) => {
        dispatch({ type: "get_expectedAcv_drilldown_data_success", json });
      },
      (error) => {
        dispatch({
          type: "get_expectedAcv_drilldown_data_failure",
          error: typeof error === "object" ? "Something went wrong" : error,
        });
      }
    );
  }, [dispatch]);

  // Step back from second level drilldown
  const stepBack = useCallback(() => {
    setSecondLevelLocation("");
    dispatch({ type: "clear_expected_acv_data" });
  }, [dispatch]);

  // CardId -> action -> handler mapping (extend this for new drilldowns).
  // Each card can have multiple drilldowns by calling onDrilldown(action, payload).
  // Memoized so handler lookup stays stable for parent memoized props.
  const cardDrilldownHandlers = useMemo(
    () => ({
      RCC_QTD_Bookings: {
        [RCC_DRILLDOWN_ACTIONS.DEFAULT]: () => handleWonDrilldown("CFQ"),
        [RCC_DRILLDOWN_ACTIONS.WON_OPPORTUNITIES]: (payload = {}) =>
          handleWonDrilldown(payload.timeFrame || "CFQ"),
      },
      RCC_YTD_Bookings: {
        [RCC_DRILLDOWN_ACTIONS.DEFAULT]: () => handleWonDrilldown("YTD"),
        [RCC_DRILLDOWN_ACTIONS.WON_OPPORTUNITIES]: (payload = {}) =>
          handleWonDrilldown(payload.timeFrame || "YTD"),
      },
      RCC_Qtr_Projection: {
        [RCC_DRILLDOWN_ACTIONS.DEFAULT]: handleQualifiedPipelineDrilldown,
        [RCC_DRILLDOWN_ACTIONS.QUALIFIED_PIPELINE]:
          handleQualifiedPipelineDrilldown,
      },
    }),
    [handleQualifiedPipelineDrilldown, handleWonDrilldown],
  );

  const getDrilldownHandler = useCallback((cardId) => {
    const actionMap = cardDrilldownHandlers[cardId];
    if (!actionMap) return undefined;

    // Compatible with:
    // - onClick={onDrilldown} (first arg is a synthetic event)
    // - onDrilldown() (no args)
    // - onDrilldown(action, payload)
    return (action, payload) => {
      const isEventLike =
        action &&
        typeof action === "object" &&
        typeof action.preventDefault === "function";

      const actionKey =
        isEventLike || !action ? RCC_DRILLDOWN_ACTIONS.DEFAULT : action;
      const handler =
        actionMap[actionKey] || actionMap[RCC_DRILLDOWN_ACTIONS.DEFAULT];
      if (typeof handler !== "function") return;

      if (isEventLike) return handler();
      return handler(payload);
    };
  }, [cardDrilldownHandlers]);

  // Return object is memoized to reduce churn for consumers that spread this hook result.
  return useMemo(
    () => ({
      // state
      drilldownLocation,
      secondLevelLocation,

      // handlers
      getDrilldownHandler,
      handleChangeLocationBack,
      handleSecondLevelDrilldown,
      stepBack,

      // data needed by modals
      drilldownOnGraphData,
      ExpectedAcvData,
      EnsembleData,
      PipelineCoverageCFQ,
      Value_Label,
      company,
      Win_Rate_Prefix,

      // constants (optional but useful for consumers)
      LOCATIONS,
    }),
    [
      drilldownLocation,
      secondLevelLocation,
      getDrilldownHandler,
      drilldownOnGraphData,
      ExpectedAcvData,
      EnsembleData,
      PipelineCoverageCFQ,
      Value_Label,
      company,
      Win_Rate_Prefix,
    ],
  );
}

